import { create } from 'zustand';
import {
  clanApi,
  type ActiveWarDTO,
  type ClanDetailDTO,
  type ClanRole,
  type CreateClanInput,
  type UpdateClanInput,
  type WarRegStatus,
} from '@/clan/clanApi';

interface ClanState {
  enabled: boolean;
  featureChecked: boolean;
  /** The caller's clan, or null if they aren't in one. undefined = not loaded. */
  myClan: ClanDetailDTO | null | undefined;
  war: ActiveWarDTO | null | undefined;
  warReg: WarRegStatus | null;
  loading: boolean;
  /** Pending outgoing join request clanId (request-policy clans), if any.
   * Tracked client-side so the browse screen can show "requested" state. */
  pendingRequestId: string | null;

  loadAvailability: () => Promise<boolean>;
  refresh: () => Promise<void>;
  refreshWar: () => Promise<void>;
  registerWar: () => Promise<void>;
  create: (input: CreateClanInput) => Promise<ClanDetailDTO>;
  join: (id: string, message?: string) => Promise<boolean>;
  cancelJoin: (id: string) => Promise<void>;
  leave: () => Promise<void>;
  kick: (userId: string) => Promise<void>;
  setRole: (userId: string, role: Exclude<ClanRole, 'leader'>) => Promise<void>;
  transfer: (userId: string) => Promise<void>;
  approve: (userId: string) => Promise<void>;
  reject: (userId: string) => Promise<void>;
  update: (patch: UpdateClanInput) => Promise<void>;
  disband: () => Promise<void>;
}

/** Throws if no clan is loaded — every mutating action needs one. */
function requireClanId(get: () => ClanState): string {
  const id = get().myClan?.id;
  if (!id) throw new Error('Not in a clan');
  return id;
}

export const useClanStore = create<ClanState>((set, get) => ({
  enabled: false,
  featureChecked: false,
  myClan: undefined,
  war: undefined,
  warReg: null,
  loading: false,
  pendingRequestId: null,

  loadAvailability: async () => {
    try {
      const { enabled } = await clanApi.status();
      set({
        enabled,
        featureChecked: true,
        ...(!enabled
          ? {
              myClan: null,
              war: null,
              warReg: null,
              loading: false,
              pendingRequestId: null,
            }
          : {}),
      });
      return enabled;
    } catch {
      set({
        enabled: false,
        featureChecked: true,
        myClan: null,
        war: null,
        warReg: null,
        loading: false,
        pendingRequestId: null,
      });
      return false;
    }
  },

  refresh: async () => {
    set({ loading: true });
    if (!(await get().loadAvailability())) return;
    try {
      const { clan, pendingRequestId } = await clanApi.me();
      // Server is authoritative for the outgoing-request state — this clears a
      // stale "requested / cancel" after the user was approved, kicked, or left.
      set({ myClan: clan, pendingRequestId: pendingRequestId ?? null, loading: false });
      void get().refreshWar();
    } catch {
      set({ loading: false });
    }
  },

  refreshWar: async () => {
    try {
      const { war, reg } = await clanApi.war();
      set({ war, warReg: reg });
    } catch {
      set({ war: null, warReg: null });
    }
  },

  registerWar: async () => {
    const id = requireClanId(get);
    await clanApi.registerWar(id);
    await get().refreshWar();
  },

  create: async (input) => {
    const { clan } = await clanApi.create(input);
    set({ myClan: clan, pendingRequestId: null });
    return clan;
  },

  join: async (id, message) => {
    const { joined } = await clanApi.join(id, message);
    if (joined) {
      await get().refresh();
    } else {
      set({ pendingRequestId: id });
    }
    return joined;
  },

  cancelJoin: async (id) => {
    await clanApi.cancelJoin(id);
    set((s) => ({ pendingRequestId: s.pendingRequestId === id ? null : s.pendingRequestId }));
  },

  leave: async () => {
    await clanApi.leave();
    set({ myClan: null });
  },

  kick: async (userId) => {
    await clanApi.kick(requireClanId(get), userId);
    await get().refresh();
  },

  setRole: async (userId, role) => {
    await clanApi.setRole(requireClanId(get), userId, role);
    await get().refresh();
  },

  transfer: async (userId) => {
    await clanApi.transfer(requireClanId(get), userId);
    await get().refresh();
  },

  approve: async (userId) => {
    await clanApi.approve(requireClanId(get), userId);
    await get().refresh();
  },

  reject: async (userId) => {
    await clanApi.reject(requireClanId(get), userId);
    await get().refresh();
  },

  update: async (patch) => {
    const { clan } = await clanApi.update(requireClanId(get), patch);
    set({ myClan: clan });
  },

  disband: async () => {
    await clanApi.disband(requireClanId(get));
    set({ myClan: null });
  },
}));
